import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { useEditQuestionnaireViewModelQuery, useEditQuestionnaireViewModelQueryVariables } from '../../generated/useEditQuestionnaireViewModelQuery';
import { questionnaireFieldsFragment } from '../../models/Questionnaire';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { questionnaireSectionFieldsFragment } from '../../models/QuestionnaireSection';
import { questionFieldsFragment } from '../../models/Question';
import { learningUnitFieldsFragment } from '../../models/LearningUnit';
import { questionnaireRespondentSessionFieldsFragment } from '../../models/QuestionnaireRespondentSession';

/**
 * Get a Questionnaire from the store.
 * @param id
 * @param options
 */
export function useEditQuestionnaireViewModel(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<useEditQuestionnaireViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<useEditQuestionnaireViewModelQuery, useEditQuestionnaireViewModelQueryVariables>(
        gql`
        query useEditQuestionnaireViewModelQuery ($id: ID!) {
            model: questionnaire(id: $id) {
                ...questionnaireFields
            }

            sections: questionnaireSections(questionnaireId: $id){
                ...questionnaireSectionFields
            }

            questions: questions(questionnaireId: $id){
                ...questionFields
            }

            questionnaireRespondentSessions: questionnaireRespondentSessions(questionnaireId: $id){
                ...questionnaireRespondentSessionFields
            }

            learningUnit: learningUnit(questionnaireId: $id){
                ...learningUnitFields
            }

        }

        ${questionnaireFieldsFragment}
        ${questionnaireSectionFieldsFragment}
        ${questionFieldsFragment}
        ${learningUnitFieldsFragment}
        ${questionnaireRespondentSessionFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
